import type { OpenTableOptions, TableBrowserModel, TableBrowserPanelModel } from '.';
import { createTableObject } from './api';
import { TableBrowserLayoutModel } from './layoutPreferences';
import { TableBrowserPanelModelImpl } from './TableBrowserPanelModel';
import { parseTableBrowserUrl, serializeTableBrowserUrl } from './tableBrowserUrl';
import type { TableBrowserUrlPanel } from './tableBrowserUrl';
import { TableCatalogModelImpl } from './TableCatalogModel';
import { ThroughputModelImpl } from './ThroughputModel';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { ObjectDefinitionResult } from '@thinkalpha/table-client';
import type { DatadogGridReplayPrivacy } from '@thinkalpha/ui-table-svelte/datadogGridReplayPrivacy';
import { inject, injectable } from 'src/features/ioc';
import { resolveDatadogGridReplayPrivacy } from 'src/features/table/datadogGridReplayPrivacy';
import { enableDatadogGridReplayCapture } from 'src/lib/featureFlags/flags/enableDatadogGridReplayCapture';
import { useNextGenProxy } from 'src/lib/featureFlags/flags/useNextGenProxy';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Page model for the admin Table Browser. Owns the catalog, the ordered set of open
 * panels, layout preferences, throughput counters, and the URL: everything that describes what is
 * being looked at lives in the address bar so a debugger can share it.
 */
@injectable()
export class TableBrowserModelImpl implements TableBrowserModel {
    readonly #disposableStack = new DisposableStack();
    readonly #log: ReactBindings['Logger'];
    readonly #currentUser: ReactBindings['CurrentUserModel'];
    readonly #navigation: ReactBindings['NavigationService'];
    readonly #tableModelFactory: ReactBindings['TableModelFactory'];
    readonly #proxyClientService: ReactBindings['ProxyClientService'];
    readonly #panels = signal<readonly TableBrowserPanelModelImpl[]>([]);
    readonly #selectedKey = signal<string | null>(null);
    readonly #datadogReplayPrivacy: ReadonlySignal<DatadogGridReplayPrivacy>;
    readonly #useNgProxy: ReadonlySignal<boolean>;
    /** The search string this model last wrote, so its own navigation is not re-applied. */
    #lastWrittenSearch: string | null = null;

    readonly catalog: TableCatalogModelImpl;
    readonly throughput: ThroughputModelImpl;
    readonly layout: TableBrowserLayoutModel;
    readonly isSuperuserCapable: ReadonlySignal<boolean>;
    readonly isSuperuserModeEnabled: ReadonlySignal<boolean>;

    constructor(
        @inject('ProxyClientService') proxyClientService: ReactBindings['ProxyClientService'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('CurrentUserModel') currentUser: ReactBindings['CurrentUserModel'],
        @inject('NavigationService') navigation: ReactBindings['NavigationService'],
        @inject('TableModelFactory') tableModelFactory: ReactBindings['TableModelFactory'],
        @inject('SyncStorageService') storage: ReactBindings['SyncStorageService'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#log = logger.getSubLogger({ name: 'admin:browser' });
        this.#currentUser = currentUser;
        this.#navigation = navigation;
        this.#tableModelFactory = tableModelFactory;
        this.#proxyClientService = proxyClientService;
        this.isSuperuserCapable = currentUser.isSuperuser;
        this.isSuperuserModeEnabled = currentUser.isSuperUserModeEnabled;
        this.#useNgProxy = featureFlagService.getSignal(useNextGenProxy);
        const replayCapture = featureFlagService.getSignal(enableDatadogGridReplayCapture);
        this.#datadogReplayPrivacy = computed(() => resolveDatadogGridReplayPrivacy(replayCapture.value));

        this.layout = new TableBrowserLayoutModel(storage, this.#log);
        this.catalog = new TableCatalogModelImpl({
            proxyClientService,
            queryObserverModelFactory,
            featureFlagService,
            isSuperuserCapable: this.isSuperuserCapable,
        });
        this.#disposableStack.use(this.catalog);
        this.throughput = new ThroughputModelImpl(proxyClientService);
        this.#disposableStack.use(this.throughput);
        this.#disposableStack.defer(() => {
            for (const panel of this.#panels.value) {
                panel[Symbol.dispose]();
            }
            this.#panels.value = [];
        });

        this.#applyUrl(navigation.location.value.search);

        // Address-bar edits and history traversal land here; our own writes are skipped.
        this.#disposableStack.defer(
            effect(() => {
                const { search } = navigation.location.value;
                if (search === this.#lastWrittenSearch) {
                    return;
                }
                untracked(() => this.#applyUrl(search));
            }),
        );
        // Every change to what is being looked at is written back, replacing the current entry.
        this.#disposableStack.defer(
            effect(() => {
                const search = serializeTableBrowserUrl({
                    panels: this.#panels.value.map((panel) => ({ sourceKey: panel.sourceKey, view: panel.view.value })),
                    search: this.catalog.search.value,
                    serverFilter: this.catalog.serverFilter.value,
                });
                untracked(() => this.#writeUrl(search));
            }),
        );
    }

    get panels(): ReadonlySignal<readonly TableBrowserPanelModel[]> {
        return this.#panels;
    }

    get selectedKey(): ReadonlySignal<string | null> {
        return this.#selectedKey;
    }

    toggleSuperuserMode(enabled: boolean): void {
        this.#currentUser.toggleSuperUserMode(enabled);
    }

    selectKey(key: string | null): void {
        this.#selectedKey.value = key;
    }

    openTable(sourceKey: string, options: OpenTableOptions = {}): TableBrowserPanelModel {
        this.#log.info({ message: 'Opening table', key: sourceKey });
        const panel = this.#createPanel({ sourceKey, view: options.view ?? { sort: null, filter: null } });
        const panels = [...this.#panels.value];
        const afterIndex =
            options.afterPanelId === undefined
                ? -1
                : panels.findIndex((candidate) => candidate.id === options.afterPanelId);
        panels.splice(afterIndex === -1 ? panels.length : afterIndex + 1, 0, panel);
        this.#panels.value = panels;
        this.#ensureSuperuserMode();
        this.catalog.refresh();
        return panel;
    }

    closePanel(panelId: string): void {
        const panel = this.#panels.value.find((candidate) => candidate.id === panelId);
        if (panel === undefined) {
            return;
        }
        this.#panels.value = this.#panels.value.filter((candidate) => candidate !== panel);
        panel[Symbol.dispose]();
    }

    soloPanel(panelId: string): void {
        for (const panel of this.#panels.value) {
            panel.setCollapsed(panel.id !== panelId);
        }
    }

    navigateTo(pathname: string): void {
        void this.#navigation.navigate({ pathname });
    }

    async defineObject(definitions: unknown): Promise<ObjectDefinitionResult> {
        const result = await createTableObject(this.#useNgProxy.value, { persist: false, definitions });
        if (result.success) {
            this.catalog.refresh();
        }
        return result;
    }

    #createPanel(urlPanel: TableBrowserUrlPanel): TableBrowserPanelModelImpl {
        return new TableBrowserPanelModelImpl({
            sourceKey: urlPanel.sourceKey,
            view: urlPanel.view,
            table: this.#tableModelFactory(),
            layout: this.layout,
            catalogKeys: this.catalog.keys,
            proxyClient: this.#proxyClientService.target.value.client,
            datadogReplayPrivacy: this.#datadogReplayPrivacy,
            log: this.#log,
        });
    }

    /** A deep link only works with the full catalog, so arriving with tables turns superuser mode on. */
    #ensureSuperuserMode(): void {
        if (this.#currentUser.isSuperuser.value && !this.#currentUser.isSuperUserModeEnabled.value) {
            this.#currentUser.toggleSuperUserMode(true);
        }
    }

    /**
     * Reconciles the open panels with the URL: entries are matched to existing panels by source key
     * in order so a panel keeps its identity (and its TableModel) across a URL round-trip; unmatched
     * panels close and new entries open.
     */
    #applyUrl(search: string): void {
        const state = parseTableBrowserUrl(search);
        if (TRACE) {
            this.#log.trace({ message: 'Applying URL', search, panels: state.panels.length });
        }
        const remaining = [...this.#panels.value];
        const next: TableBrowserPanelModelImpl[] = [];
        for (const entry of state.panels) {
            const index = remaining.findIndex((panel) => panel.sourceKey === entry.sourceKey);
            if (index === -1) {
                next.push(this.#createPanel(entry));
                continue;
            }
            const [panel] = remaining.splice(index, 1);
            panel.applyView(entry.view);
            next.push(panel);
        }
        for (const panel of remaining) {
            panel[Symbol.dispose]();
        }
        const unchanged =
            next.length === this.#panels.value.length &&
            next.every((panel, index) => panel === this.#panels.value[index]);
        if (!unchanged) {
            this.#panels.value = next;
        }
        this.catalog.setSearch(state.search);
        this.catalog.setServerFilter(state.serverFilter);
        if (state.panels.length > 0) {
            this.#ensureSuperuserMode();
        }
    }

    #writeUrl(search: string): void {
        const location = this.#navigation.location.value;
        if (location.search === search) {
            this.#lastWrittenSearch = search;
            return;
        }
        this.#lastWrittenSearch = search;
        if (TRACE) {
            this.#log.trace({ message: 'Writing URL', search });
        }
        void this.#navigation.navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true });
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
